// src/components/MyLibraryCategoriesPage.js
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { auth } from '../firebaseConfig';
import { API_BASE_URL } from '../services/api';
// API_BASE_URL used for category background images (same source as Categories.js)
import './MyLibraryCategories.css';

/* ─── Helpers ────────────────────────────────────────────────────────────── */

/* ─── Component ──────────────────────────────────────────────────────────── */

function MyLibraryCategoriesPage() {
  const navigate           = useNavigate();
  const { user }           = useContext(AuthContext);
  const { selectedRegion } = useRegion();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadCategories = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${API_BASE_URL}/private-materials/categories`, {
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region':      selectedRegion || 'vquest',
          'x-user-role':   user.role  || 'user',
          'x-user-id':     user.uid,
          'x-user-email':  user.email,
        },
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('❌ Error loading private categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, selectedRegion]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const filtered = categories.filter((c) =>
    c.CategoryName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ── Loading ── */
  if (loading) return (
    <div className="mlc-page">
      <div className="mlc-header">
        <button className="mlc-back-btn" onClick={() => navigate('/user/dashboard')}>← Back</button>
        <h1>My Library</h1>
      </div>
      <div className="mlc-loading">
        <div className="mlc-loading__spinner" />
        <p>Loading your private materials…</p>
      </div>
    </div>
  );

  return (
    <div className="mlc-page">

      {/* Header */}
      <div className="mlc-header">
        <button className="mlc-back-btn" onClick={() => navigate('/user/dashboard')}>← Back to Dashboard</button>
        <div className="mlc-header__text">
          <h1 className="mlc-title">My Library</h1>
          <p className="mlc-subtitle">
            Your private approved materials — only visible to your account
          </p>
        </div>
        <button className="mlc-refresh-btn" onClick={loadCategories}>🔄 Refresh</button>
      </div>

      {/* Stats bar */}
      <div className="mlc-stats">
        <div className="mlc-stat">
          <span className="mlc-stat__value">{categories.length}</span>
          <span className="mlc-stat__label">Categories</span>
        </div>
        <div className="mlc-stat">
          <span className="mlc-stat__value">
            {categories.reduce((sum, c) => sum + (c.MaterialCount || 0), 0)}
          </span>
          <span className="mlc-stat__label">Private Materials</span>
        </div>
        <div className="mlc-stat mlc-stat--badge">
          <span>🔒 Private Only</span>
        </div>
      </div>

      {/* Search */}
      <div className="mlc-search-wrap">
        <input
          type="text"
          className="mlc-search"
          placeholder="Search categories…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="mlc-search-clear" onClick={() => setSearchTerm('')}>✕</button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mlc-error">
          <span>⚠️ {error}</span>
          <button onClick={loadCategories}>Retry</button>
        </div>
      )}

      {/* Empty state */}
      {!error && filtered.length === 0 && (
        <div className="mlc-empty">
          <div className="mlc-empty__icon">🔒</div>
          {searchTerm ? (
            <>
              <h3>No categories match "{searchTerm}"</h3>
              <p>Try a different search term.</p>
            </>
          ) : (
            <>
              <h3>No private materials yet</h3>
              <p>
                When you submit a private composite request and it's approved by an admin,
                it will appear here organised by category.
              </p>
              <button
                className="mlc-empty__btn"
                onClick={() => navigate('/composite-requests')}
              >
                + Build a Composite
              </button>
            </>
          )}
        </div>
      )}

      {/* Category grid */}
      {filtered.length > 0 && (
        <div className="mlc-grid">
          {filtered.map((cat) => {
            // Use the real category image from the API — same URL pattern as Categories.js
            const imageUrl = `${API_BASE_URL}/category-images/${encodeURIComponent(cat.CategoryName || cat.QuantityCatId)}`;
            return (
              <div
                key={cat.QuantityCatId}
                className="mlc-card"
                style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.42), rgba(0,0,0,0.62)), url("${imageUrl}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  // Fallback colour shown while image loads or if it 404s
                  backgroundColor: '#2D7A70',
                }}
                onClick={() => navigate(`/user/my-library/${encodeURIComponent(cat.QuantityCatId)}`, {
                  state: { categoryName: cat.CategoryName || cat.QuantityCatId }
                })}
              >
                <div className="mlc-card__body">
                  <h3 className="mlc-card__name">{cat.CategoryName || cat.QuantityCatId}</h3>
                  <span className="mlc-card__count">
                    {cat.MaterialCount} material{cat.MaterialCount !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="mlc-card__arrow">→</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MyLibraryCategoriesPage;